<script lang="ts">
	import {
		type Achievement,
		type AchievementHeadlineInfo,
		type CollectAchievementResponse
	} from '../../../../domain/Achievement';
	import AchievementProgress from '$components/home/achievement/elements/old/AchievementProgress.svelte';
	import AchievementBody from '$components/home/achievement/elements/old/AchievementBody.svelte';
	import AchievementCollectableFooter
		from '$components/home/achievement/elements/old/AchievementCollectableFooter.svelte';
	import {
		calculateExpirationStatus,
		formatPreviousDayForAriaLabel,
		formatPreviousDayWithDayAndMonth
	} from '$services/DateFormatter';
	import { createEventDispatcher } from 'svelte';
	import { ExpiryStatus } from '../../../../domain/ExpiryStatus';
	import { sheetV1 } from '@otto-ec/otto-components/sheet';
	import AchievementBaseCard from '$components/home/achievement/AchievementBaseCard.svelte';

	export let achievement: Achievement;
	export let index: number;
	export let isHorizontal: boolean = false;
	export let display: 'row' | 'grid';

	const getHeadline = (expirationDate: string): AchievementHeadlineInfo => {
		const expirationStatus = calculateExpirationStatus(expirationDate);

		if (expirationStatus === ExpiryStatus.TOMORROW) {
			return {
				text: 'Nur bis morgen'
			};
		} else if (expirationStatus === ExpiryStatus.TODAY) {
			return {
				text: 'Nur noch heute'
			};
		} else {
			const visibleDate = formatPreviousDayWithDayAndMonth(expirationDate);
			const accesibleDate = formatPreviousDayForAriaLabel(expirationDate);

			return {
				text: `Nur bis ${visibleDate}`,
				ariaLabel: `Nur bis ${accesibleDate}`
			};
		}
	};

	const headlineInfo = getHeadline(achievement.content.card.expirationDate);

	export let onclick: Function;
	const dispatch = createEventDispatcher();

	let loading = false;
	let disabled = false;

	const collect = async () => {
		loading = true;
		disabled = true;
		onclick();

		fetch(`/up-homeui/gateway/achievement/collect?achievementId=${achievement.achievementId}`, {
			method: 'POST'
		}).then(async response => {
			if (response.status != 200) {
				await fetch(`/up-homeui/gateway/achievement/collect/metrics`, { method: 'POST' });
				openErrorSheet();
				return;
			}

			try {
				const collectAchievementResponse: CollectAchievementResponse = await response.json();
				if (collectAchievementResponse.success) {
					openSuccessSheet();
					dispatch('achievementCollected', achievement);
				} else {
					openErrorSheet();
				}
			} finally {
				loading = false;
				disabled = false;
			}
		}).catch(() => {
			openErrorSheet();
		}).finally(() => {
			loading = false;
			disabled = false;
		});

	};

	const openErrorSheet = () => {
		const props = {
			id: 'collect-error-sheet',
			url: '/up-homeui/sheet/achievement/collected/error',
			headline: 'Etwas ist schief gelaufen',
			noContentPadding: true,
			fullHeight: true,
			lockMobileHeight: true,
			initialMobileHeight: '95vh'
		};

		sheetV1.create({ open: true, ...props });
	};
	const openSuccessSheet = () => {
		const url = `/up-homeui/sheet/achievement/collect/success?achievementId=${achievement.achievementId}&achievementName=${achievement.name}&compensationAmount=${achievement.content.card.compensationAmount}&showFeedback=${achievement.content.showFeedback}`;
		const props = {
			id: 'collect-success-sheet',
			url: url,
			headline: '',
			noContentPadding: true,
			fullHeight: true,
			lockMobileHeight: true,
			initialMobileHeight: '95vh'
		};

		sheetV1.create({ open: true, ...props });
	};

</script>
<AchievementBaseCard achievement={achievement} display={display}>
	<oc-card-v2
		as-button=""
		class="card"
		class:card__disabled={disabled}
		data-testid="achievement-collectable-card"
		id="achievement-card-{index+1}"
		on:click={collect}
	>
		<div
			class="oc-p-75 card__content"
			class:card__content--horizontal={isHorizontal}
			data-testid="achievement-collectable-card-content"
		>
			<div class="card__body">
				<AchievementProgress achievement={achievement} />
				<AchievementBody
					achievement={achievement}
					headline={headlineInfo.text}
					headlineAriaLabel={headlineInfo.ariaLabel ? headlineInfo.ariaLabel : undefined}
				/>
			</div>
			<AchievementCollectableFooter compensationAmount={achievement.content.card.compensationAmount}
				isHorizontal={isHorizontal} loading={loading} />
		</div>
	</oc-card-v2>
</AchievementBaseCard>

<style lang="scss">
  .card {
    &__content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;

      &--horizontal {
        flex-direction: row;
      }
    }

    &__disabled {
      pointer-events: none;
    }

    &__body {
      display: flex;
      align-items: center;
    }

  }
</style>
